/**
 * Where the account pages live.
 *
 * Absolute paths for linking to them, and the segments the router nests under
 * `/account`. Kept in one place so a renamed route cannot leave a stale link
 * behind somewhere else in the app.
 */

export const ACCOUNT_ROOT = '/account';

export const ACCOUNT_SEGMENTS = {
  signUp: 'signup',
  logIn: 'login',
  verifyEmail: 'verify-email',
  forgotPassword: 'forgot-password',
  resetPassword: 'reset-password',
  /** AuthKit's redirectUri: where WorkOS sends people back to. */
  callback: 'callback',
  /** The sign-up questions; `?edit=1` reopens them from the Account page. */
  welcome: 'welcome',
} as const;

type AccountPage = keyof typeof ACCOUNT_SEGMENTS;

export const ACCOUNT_PATHS = Object.fromEntries(
  Object.entries(ACCOUNT_SEGMENTS).map(([page, segment]) => [page, `${ACCOUNT_ROOT}/${segment}`]),
) as { [Page in AccountPage]: string };

/**
 * An account page, including its children. The account pages are drawn over
 * the app, which stays mounted under them (AppShell), so App's own global
 * listeners have to know when they are not the page being used.
 */
export const isAccountPath = (pathname: string): boolean =>
  pathname === ACCOUNT_ROOT || pathname.startsWith(`${ACCOUNT_ROOT}/`);
